import * as Sentry from '@sentry/react';

import config from '@/config';
import { apiErrorMessage, isGatewayFailure, isWireLevelFailure } from '@/core/api';

// Same wording as the acceptance gate (AgreementAcceptance), for the same two
// transport failures: blaming the expert's input for either would be wrong.
export const PROFILE_SAVE_CONNECTION_ERROR = `We could not reach ${config.productName}. Check your connection and try again.`;
export const PROFILE_SAVE_UNAVAILABLE_ERROR = `${config.productName} is temporarily unavailable. Please try again in a moment.`;

// Profile.tsx and ProfilePreview.tsx both offer these actions outside any form.
export const PICTURE_SAVE_ERROR = 'An error occurred when updating your profile picture.';
export const COVER_SAVE_ERROR = 'An error occurred when updating your cover image.';
export const CV_ATTACH_ERROR = 'An error occurred when attaching your CV.';
export const CV_REMOVE_ERROR = 'An error occurred when removing your CV.';

export function profileSaveErrorMessage(err: unknown, fallback: string): string {
  if (isWireLevelFailure(err)) return PROFILE_SAVE_CONNECTION_ERROR;
  if (isGatewayFailure(err)) return PROFILE_SAVE_UNAVAILABLE_ERROR;
  return apiErrorMessage(err, fallback);
}

/**
 * Reports a failed profile save and tells the expert, returning the message shown.
 *
 * Reporting is not optional: these failures only ever reached Sentry by escaping
 * as an onunhandledrejection (WEBAPP-CCK, WEBAPP-CCM), so handling one without
 * capturing it would make the next instance invisible. See ENG-5535.
 *
 * Callers inside a redux-form submit handler must still throw
 * `new SubmissionError({ _error: message })` afterwards. That, not this call, is
 * what stops the dialog closing and DeprecatedSaveForm's success toast.
 */
export function reportProfileSaveFailure(
  err: unknown,
  notify: (message: string, messageType: string) => unknown,
  fallback: string
): string {
  Sentry.captureException(err);
  const message = profileSaveErrorMessage(err, fallback);
  notify(message, 'error');
  return message;
}
